import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api, { formatApiError } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { KeyRound, LogIn, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { createT } from '@/lib/i18n';

export default function SetPasswordPage() {
  const { setPassword, forgotPassword } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const token = searchParams.get('token') || '';
  const lang = searchParams.get('lang') || 'ru';
  const t = createT(lang);

  const [password, setPasswordVal] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Состояние ссылки/пользователя — определяет, какие подсказки/кнопки показать.
  // null = ещё не проверено
  const [linkState, setLinkState] = useState(null);

  // Pre-check инвайт-ссылки сразу при заходе. Без авторизации.
  useEffect(() => {
    if (!email || !token) {
      setError(t('setpw.bad_link'));
      return;
    }
    (async () => {
      try {
        const { data } = await api.get('/auth/check-invite', { params: { email, token } });
        setLinkState(data);
        if (data && data.has_password && !data.valid_token) {
          // Пароль уже задан, а ссылка не подходит → понятное сообщение.
          setError(
            lang === 'en'
              ? 'You already have a password. Please log in, or request a new link if you forgot it.'
              : 'У вас уже задан пароль. Войдите на главной, либо запросите новую ссылку, если забыли его.'
          );
        } else if (data && !data.valid_token) {
          setError(
            lang === 'en'
              ? 'This invitation link has expired or has already been used. Request a new one below.'
              : 'Эта ссылка устарела или уже была использована. Запросите новую кнопкой ниже.'
          );
        }
      } catch {/* не критично — пользователь увидит обычную ошибку при submit */}
    })();
  }, [email, token, t, lang]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { setError(t('setpw.mismatch')); return; }
    if (password.length < 6) { setError(t('setpw.too_short')); return; }
    setLoading(true); setError('');
    try {
      await setPassword(email, token, password);
      toast.success(t('setpw.success'));
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const requestNewLink = async () => {
    if (!email) return;
    setLoading(true);
    try {
      await forgotPassword(email);
      toast.success(
        lang === 'en'
          ? 'Request sent. The admin will send you a fresh link by email.'
          : 'Запрос отправлен. Администратор пришлёт вам новую ссылку на почту.'
      );
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const showHasPassword = !!(linkState && linkState.has_password && !linkState.valid_token);
  const showRequestNew = !!(linkState && !linkState.valid_token);
  const formDisabled = loading || !email || !token || (linkState && !linkState.valid_token);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-8" data-testid="set-password-page">
      <div className="w-full max-w-sm bg-white border border-slate-200 rounded-sm p-8 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <h1 className="font-heading text-xl font-bold tracking-tight text-slate-900">{t('setpw.title')}</h1>
        </div>

        {email && (
          <p className="text-sm text-slate-500 mb-4">
            {t('setpw.account')}: <span className="font-medium text-slate-700">{email}</span>
          </p>
        )}

        {error && (
          <div className="bg-rose-50 text-rose-700 border border-rose-200 px-3 py-2 rounded-sm mb-4 text-sm" data-testid="set-password-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-slate-700">{t('setpw.new_password')}</Label>
            <Input type="password" value={password} onChange={e => setPasswordVal(e.target.value)}
              placeholder={t('setpw.min6')} required data-testid="set-password-input"
              className="border-slate-300 rounded-sm" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-slate-700">{t('setpw.confirm')}</Label>
            <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
              placeholder={t('setpw.repeat')} required data-testid="set-password-confirm"
              className="border-slate-300 rounded-sm" />
          </div>
          <Button type="submit" disabled={formDisabled} data-testid="set-password-submit"
            className="w-full bg-[#0046AD] hover:bg-[#003A8C] rounded-sm font-semibold">
            <KeyRound className="w-4 h-4 mr-2" /> {loading ? t('setpw.saving') : t('setpw.btn')}
          </Button>
        </form>

        {/* Выходы из тупика: уже задан пароль / устаревшая ссылка */}
        {(showHasPassword || showRequestNew) && (
          <div className="mt-5 pt-4 border-t border-slate-200 space-y-2">
            {showHasPassword && (
              <Button type="button" variant="outline"
                onClick={() => { window.location.href = `/?lang=${lang}`; }}
                data-testid="set-password-go-login"
                className="w-full rounded-sm border-slate-300">
                <LogIn className="w-4 h-4 mr-2" />
                {lang === 'en' ? 'Go to login' : 'Перейти ко входу'}
              </Button>
            )}
            {showRequestNew && (
              <Button type="button" variant="outline" onClick={requestNewLink}
                disabled={loading}
                data-testid="set-password-request-new"
                className="w-full rounded-sm border-slate-300">
                <Mail className="w-4 h-4 mr-2" />
                {lang === 'en' ? 'Request a new link' : 'Запросить новую ссылку'}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
